"use client";

import FavoriteIconButton from "@/components/card/card-component/FavoriteIconButton";
import Image from "next/image";
import CardStatusBadges from "./card-component/CardStatusBadges";
import RatingReviewBadge from "./card-component/RatingReviewBadge";
import PriceDisplay from "./card-component/PriceDisplay";
import { memo } from "react";
import { Link } from "@/i18n/navigation";
import { cn, FALLBACK_FEATURE_IMAGE } from "@/lib/utils";
import { Typography } from "../Typography";
import { ServiceIcon } from "../services/ServiceMeta";
import { TourActCarouselItem } from "../main-services/tour-act/tour.type";
import { useRouter } from "next/navigation";
import { useTourHref } from "@/hooks/url-state/useActivityQuery";

export type TourActivityCardProps = {
  data: TourActCarouselItem;
  className?: string;
};

const TourActivityCard = ({ data, className }: TourActivityCardProps) => {
  const router = useRouter();
  const finalHref = useTourHref(data);

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest("button") || target.closest("a")) {
      return;
    }
    router.push(finalHref as never);
  };

  const cardContent = (
    <div className="relative flex h-[400px] w-full flex-col overflow-hidden rounded-2xl">
      <Image
        src={data.feature ?? FALLBACK_FEATURE_IMAGE}
        alt={data.feature ?? data.title ?? "N/A"}
        fill
        unoptimized
        className="object-cover transform-gpu transition-transform duration-500 ease-out group-hover:scale-[1.03]"
        sizes="(max-width: 640px) 100vw, 320px"
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/20" />

      <FavoriteIconButton
        itemId={Number(data.id)}
        svcType="tour_act"
        ariaLabel="Save service"
        active={data.isFavorite}
        className="absolute left-2 top-2 z-10"
      />

      <CardStatusBadges
        isInstantConfirm={data.isInstantConfirm}
        className="!absolute !top-2 !right-2"
      />

      <div className="relative z-10 mt-auto flex min-w-0 flex-col gap-1.5 px-3 pb-3">
        <section className="flex justify-between">
          <div>
            <RatingReviewBadge
              rating={data.overallReview?.overallRating}
              reviewCount={data.totalReviews}
            />
          </div>

          <div className="z-10 flex items-center gap-2">
            <CardStatusBadges disPercent={Number(data.disPercent)} />
          </div>
        </section>

        <Typography
          variant="subtitle1"
          className="line-clamp-2 text-white h-12"
        >
          {data.title}
        </Typography>

        <div className="flex min-w-0 items-end justify-between gap-2">
          <div className="flex min-w-0 flex-col gap-1">
            {data.duration ? (
              <div className="flex min-w-0 items-center gap-1">
                <ServiceIcon
                  icon="clock"
                  className="h-3.5 w-3.5 shrink-0 text-white/90"
                />
                <Typography variant="body2" className="truncate text-white/90">
                  {data.duration}
                </Typography>
              </div>
            ) : null}

            {data.located ? (
              <div className="flex min-w-0 items-center gap-1 line-clamp-1">
                <ServiceIcon
                  icon="location"
                  className="h-3.5 w-3.5 shrink-0 text-white/90"
                />
                <Typography variant="body2" className="truncate text-white/90">
                  {data.located}
                </Typography>
              </div>
            ) : null}
          </div>

          <div
            style={{
              background: "#FFFFFF40",
              borderRadius: "12px",
              opacity: 1,
              paddingTop: "4px",
              paddingRight: "8px",
              paddingBottom: "4px",
              paddingLeft: "8px",
              WebkitBackdropFilter: "blur(4px)",
              backdropFilter: "blur(4px)",
              boxShadow:
                "-1px -1px 1px 0px #FFFFFF3D inset, 1px 1px 1px 0px #FFFFFF3D inset",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              justifyContent: "center",
              marginLeft: "auto",
            }}
          >
            <PriceDisplay
              price={Number(data.price)}
              salePrice={Number(data.salePrice)}
              className="shrink-0"
              priceColor="text-white"
              salePriceColor="text-primary"
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <article
      className={cn(
        "group relative w-full rounded-2xl shadow-product-card border",
        className,
      )}
      onClick={handleCardClick}
    >
      {finalHref ? (
        <Link
          href={finalHref as never}
          className="block h-full"
          aria-label={`Open ${data.title}`}
        >
          {cardContent}
        </Link>
      ) : (
        cardContent
      )}
    </article>
  );
};

export default memo(TourActivityCard);
